import { useState } from "react";
import {
  Container,
  Paper,
  Title,
  Button,
  Stack,
  Table,
  Badge,
  Group,
  FileInput,
  Text,
  ActionIcon,
  Tooltip,
  Box,
} from "@mantine/core";
import { IconUpload, IconTrash } from "@tabler/icons-react";
import { useVendorDocuments, useUploadVendorDocument, useDeleteVendorDocument } from "../hooks/useVendorDocuments";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

// Define all required document types
type DocumentType = "BUSINESS_REGISTRATION" | "COMPANY_PROFILE" | "TAX_CLEARANCE" | "INSURANCE" | "FINANCIAL_STATEMENT";

const REQUIRED_DOCUMENT_TYPES: Array<{ value: DocumentType; label: string; mandatory: boolean }> = [
  { value: "BUSINESS_REGISTRATION", label: "Business Registration", mandatory: true },
  { value: "COMPANY_PROFILE", label: "Company Profile", mandatory: true },
  { value: "TAX_CLEARANCE", label: "Tax Clearance", mandatory: false },
  { value: "INSURANCE", label: "Insurance", mandatory: false },
  { value: "FINANCIAL_STATEMENT", label: "Financial Statement", mandatory: false },
];

export function VendorDocuments() {
  const { data: documents, isLoading } = useVendorDocuments();
  const uploadDocument = useUploadVendorDocument();
  const deleteDocument = useDeleteVendorDocument();
  
  // Track file selections and upload states per document type
  const [fileSelections, setFileSelections] = useState<Record<string, File | null>>({});
  const [uploadingTypes, setUploadingTypes] = useState<Set<string>>(new Set());

  const handleFileChange = (documentType: DocumentType, file: File | null) => {
    setFileSelections((prev) => ({
      ...prev,
      [documentType]: file,
    }));
  };

  const handleUpload = async (documentType: DocumentType) => {
    const file = fileSelections[documentType];
    if (!file) {
      notifications.show({
        title: "Error",
        message: "Please select a file to upload",
        color: "red",
      });
      return;
    }

    setUploadingTypes((prev) => new Set(prev).add(documentType));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);

    try {
      await uploadDocument.mutateAsync(formData);
      setFileSelections((prev) => {
        const updated = { ...prev };
        delete updated[documentType];
        return updated;
      });
    } catch (error) {
      // Error handled by mutation
    } finally {
      setUploadingTypes((prev) => {
        const updated = new Set(prev);
        updated.delete(documentType);
        return updated;
      });
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await deleteDocument.mutateAsync(documentId);
    } catch (error) {
      // Error handled by mutation
    }
  };

  // Create a map of uploaded documents by type
  const documentsByType = new Map(
    (documents || []).map((doc) => [doc.documentType, doc])
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="md" mb="md">
            <Title order={2}>Vendor Documents</Title>
            <Text size="sm" c="dimmed">
              Please upload the required documents to complete your registration. 
              Documents marked as "Required" are mandatory for account approval.
            </Text>
          </Stack>

          {isLoading ? (
            <Text c="dimmed">Loading documents...</Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Document Type</Table.Th>
                  <Table.Th>File Information</Table.Th>
                  <Table.Th>Upload</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {REQUIRED_DOCUMENT_TYPES.map((docType) => {
                  const uploadedDoc = documentsByType.get(docType.value);
                  const isUploading = uploadingTypes.has(docType.value);
                  const hasFileSelected = !!fileSelections[docType.value];

                  return (
                    <Table.Tr key={docType.value}>
                      <Table.Td>
                        <Group gap="xs" align="center">
                          <Text fw={500}>{docType.label}</Text>
                          {docType.mandatory && (
                            <Badge color="red" size="xs" variant="light">
                              Required
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {uploadedDoc ? (
                          <Box>
                            <Text size="sm" fw={500}>{uploadedDoc.fileName}</Text>
                            <Text size="xs" c="dimmed">
                              {(uploadedDoc.fileSize / 1024).toFixed(2)} KB • {dayjs(uploadedDoc.uploadedAt).format("MMM DD, YYYY")}
                            </Text>
                          </Box>
                        ) : (
                          <Text size="sm" c="dimmed">No file uploaded</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" align="flex-start">
                          <FileInput
                            placeholder="Select file"
                            size="xs"
                            value={fileSelections[docType.value] || null}
                            onChange={(file) => handleFileChange(docType.value, file)}
                            disabled={isUploading}
                            style={{ flex: 1, minWidth: 150 }}
                          />
                          <Button
                            size="xs"
                            color="green"
                            leftSection={<IconUpload size={14} />}
                            onClick={() => handleUpload(docType.value)}
                            loading={isUploading}
                            disabled={!hasFileSelected || isUploading}
                          >
                            Upload
                          </Button>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {uploadedDoc && (
                          <Tooltip label="Delete">
                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={() => handleDelete(uploadedDoc.id)}
                              loading={deleteDocument.isPending}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}

